import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Search, Globe, Route, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const DesktopNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [autoCollapseTimeout, setAutoCollapseTimeout] = useState<NodeJS.Timeout | null>(null);

  // Auto-collapse on map page after 10 seconds
  useEffect(() => {
    if (location.pathname === '/map') {
      // Clear existing timeout
      if (autoCollapseTimeout) {
        clearTimeout(autoCollapseTimeout);
      }
      
      // Set new timeout to collapse after 10 seconds
      const timeout = setTimeout(() => {
        setIsCollapsed(true);
      }, 10000);
      
      setAutoCollapseTimeout(timeout);
      
      return () => {
        if (timeout) clearTimeout(timeout);
      };
    } else {
      // Reset to expanded state on other pages
      setIsCollapsed(false);
      if (autoCollapseTimeout) {
        clearTimeout(autoCollapseTimeout);
        setAutoCollapseTimeout(null);
      }
    }
  }, [location.pathname]);

  const handleMouseEnter = () => {
    setIsCollapsed(false);
  };

  const handleMouseLeave = () => {
    // Only auto-collapse on map page
    if (location.pathname === '/map') {
      const timeout = setTimeout(() => {
        setIsCollapsed(true);
      }, 2000); // Collapse after 2 seconds of no hover
      setAutoCollapseTimeout(timeout);
    }
  };

  const navItems = [
    {
      id: 'explore',
      label: 'Explorer',
      icon: <Search className="w-5 h-5" />,
      path: '/',
      isActive: location.pathname === '/'
    },
    {
      id: 'map',
      label: 'Visites',
      icon: <Globe className="w-5 h-5" />,
      path: '/map',
      isActive: location.pathname === '/map'
    },
    {
      id: 'tours',
      label: 'Tours',
      icon: <Route className="w-5 h-5" />,
      path: '/tours',
      isActive: location.pathname === '/tours'
    }
  ];

  // Only show on desktop devices
  if (isMobile) return null;

  return (
    <div 
      className={`fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-black/90 backdrop-blur-sm border-r border-gray-800 transition-all duration-300 ${
        isCollapsed ? 'w-6 rounded-r-2xl' : 'w-20 rounded-r-2xl'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isCollapsed ? (
        // Collapsed state - show only arrow
        <div className="flex items-center justify-center h-16 w-full">
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      ) : (
        // Expanded state - show full navigation
        <div className="flex flex-col items-center gap-1 p-2">
          {navItems.map((item) => (
            <Button
              key={item.id}
              variant="ghost"
              size="sm"
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 h-auto py-3 px-2 rounded-xl transition-all duration-200 ${
                item.isActive 
                  ? 'text-white bg-white/20' 
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className={`transition-transform duration-200 ${
                item.isActive ? 'scale-110' : 'hover:scale-105'
              }`}>
                {item.icon}
              </div>
              <span className="text-xs font-medium">
                {item.label}
              </span>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DesktopNavigation;